import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Check, Copy, Trash2 } from "lucide-react"
import * as React from "react"
import { toast } from "sonner"
import { authApi } from "@/api/auth"
import { ApiError } from "@/api/client"
import { type OrgProfile, type OrgRole, orgsApi } from "@/api/orgs"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { PageHeader } from "@/components/dashboard/page-header"
import { useAuth } from "@/hooks/use-auth"
import { formatDate } from "@/lib/utils"

const ROLE_LABEL: Record<OrgRole, string> = {
  owner: "Sahip",
  admin: "Yönetici",
  recruiter: "Recruiter",
  viewer: "İzleyici",
}

export function SettingsPage() {
  const { user, activeOrg } = useAuth()
  const canManage = activeOrg ? activeOrg.role === "owner" || activeOrg.role === "admin" : false

  return (
    <>
      <PageHeader title="Ayarlar" />
      <div className="space-y-6 p-4 sm:p-6">
        <Card>
          <CardContent className="space-y-2 p-6 text-sm">
            <div className="text-muted-foreground">Hesap</div>
            <div>{user?.email}</div>
            {user?.full_name && <div className="text-muted-foreground">{user.full_name}</div>}
          </CardContent>
        </Card>

        <OrgProfileSection canEdit={canManage} />
        <MembersSection canManage={canManage} />
        {canManage && <InvitationsSection />}
      </div>
    </>
  )
}

function OrgProfileSection({ canEdit }: { canEdit: boolean }) {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ["org-current"], queryFn: () => orgsApi.current() })
  const [draft, setDraft] = React.useState<Partial<OrgProfile> | null>(null)
  const current = q.data?.data ?? null

  React.useEffect(() => {
    if (current && draft === null) setDraft(current)
  }, [current, draft])

  const update = useMutation({
    mutationFn: (body: Partial<OrgProfile>) => orgsApi.update(body),
    onSuccess: (res) => {
      toast.success("Bilgiler güncellendi.")
      qc.invalidateQueries({ queryKey: ["org-current"] })
      qc.invalidateQueries({ queryKey: ["auth-me"] })
      setDraft(res.data)
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : "Güncellenemedi."),
  })

  if (!current || !draft) return null
  const careerUrl = `${window.location.origin}/careers/${current.career_page_slug}`

  return (
    <Card>
      <CardContent className="space-y-4 p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-medium">Şirket profili</h2>
          <Badge variant="outline">Plan: {current.plan}</Badge>
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Şirket adı</Label>
            <Input
              disabled={!canEdit}
              value={draft.name ?? ""}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Web sitesi</Label>
            <Input
              disabled={!canEdit}
              value={draft.website ?? ""}
              onChange={(e) => setDraft({ ...draft, website: e.target.value })}
              placeholder="https://"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Logo URL</Label>
            <Input
              disabled={!canEdit}
              value={draft.logo_url ?? ""}
              onChange={(e) => setDraft({ ...draft, logo_url: e.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Kariyer sayfası slug'ı</Label>
            <Input
              disabled={!canEdit}
              value={draft.career_page_slug ?? ""}
              onChange={(e) => setDraft({ ...draft, career_page_slug: e.target.value })}
            />
          </div>
        </div>
        <Alert>
          <AlertDescription className="flex items-center justify-between gap-2">
            <span className="break-all text-xs">{careerUrl}</span>
            <CopyButton text={careerUrl} />
          </AlertDescription>
        </Alert>
        {canEdit && (
          <div className="flex justify-end">
            <Button
              disabled={update.isPending}
              onClick={() =>
                update.mutate({
                  name: draft.name,
                  website: draft.website,
                  logo_url: draft.logo_url,
                  career_page_slug: draft.career_page_slug,
                })
              }
            >
              Kaydet
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function MembersSection({ canManage }: { canManage: boolean }) {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ["org-members"], queryFn: () => orgsApi.members() })

  const setRole = useMutation({
    mutationFn: (vars: { id: string; role: Exclude<OrgRole, "owner"> }) =>
      orgsApi.setMemberRole(vars.id, vars.role),
    onSuccess: () => {
      toast.success("Rol güncellendi.")
      qc.invalidateQueries({ queryKey: ["org-members"] })
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : "Güncellenemedi."),
  })

  const remove = useMutation({
    mutationFn: (id: string) => orgsApi.removeMember(id),
    onSuccess: () => {
      toast.success("Üye kaldırıldı.")
      qc.invalidateQueries({ queryKey: ["org-members"] })
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : "Kaldırılamadı."),
  })

  return (
    <Card>
      <CardContent className="space-y-3 p-6">
        <h2 className="text-sm font-medium">Takım üyeleri</h2>
        {q.isLoading && (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 py-2">
                <Skeleton className="size-8 rounded-full" />
                <Skeleton className="h-4 flex-1" />
              </div>
            ))}
          </div>
        )}
        <ul className="divide-y">
          {q.data?.data.map((m) => (
            <li key={m.id} className="flex items-center justify-between gap-3 py-2 text-sm">
              <div>
                <div className="font-medium">{m.full_name || m.email}</div>
                <div className="text-xs text-muted-foreground">{m.email}</div>
              </div>
              <div className="flex items-center gap-2">
                {m.role === "owner" ? (
                  <Badge>Sahip</Badge>
                ) : canManage ? (
                  <Select
                    value={m.role}
                    onValueChange={(v) =>
                      setRole.mutate({ id: m.id, role: v as Exclude<OrgRole, "owner"> })
                    }
                  >
                    <SelectTrigger className="h-8 w-[130px] text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Yönetici</SelectItem>
                      <SelectItem value="recruiter">Recruiter</SelectItem>
                      <SelectItem value="viewer">İzleyici</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Badge variant="outline">{ROLE_LABEL[m.role]}</Badge>
                )}
                {canManage && m.role !== "owner" && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (confirm(`${m.email} üyeliğini kaldır?`)) remove.mutate(m.id)
                    }}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  )
}

function InvitationsSection() {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ["org-invitations"], queryFn: () => orgsApi.invitations() })
  const [email, setEmail] = React.useState("")
  const [role, setRole] = React.useState<Exclude<OrgRole, "owner">>("recruiter")

  const send = useMutation({
    mutationFn: () => authApi.invite({ email: email.trim(), role }),
    onSuccess: () => {
      toast.success("Davet gönderildi.")
      setEmail("")
      qc.invalidateQueries({ queryKey: ["org-invitations"] })
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : "Gönderilemedi."),
  })

  const revoke = useMutation({
    mutationFn: (id: string) => orgsApi.revokeInvitation(id),
    onSuccess: () => {
      toast.success("Davet iptal edildi.")
      qc.invalidateQueries({ queryKey: ["org-invitations"] })
    },
  })

  return (
    <Card>
      <CardContent className="space-y-4 p-6">
        <h2 className="text-sm font-medium">Davetler</h2>
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[200px] space-y-1.5">
            <Label>E-posta</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="kisi@firma.com"
            />
          </div>
          <div className="w-40 space-y-1.5">
            <Label>Rol</Label>
            <Select value={role} onValueChange={(v) => setRole(v as Exclude<OrgRole, "owner">)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Yönetici</SelectItem>
                <SelectItem value="recruiter">Recruiter</SelectItem>
                <SelectItem value="viewer">İzleyici</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={() => send.mutate()} disabled={!email.trim() || send.isPending}>
            Davet et
          </Button>
        </div>

        <div>
          {q.data?.data.length === 0 && (
            <p className="text-xs text-muted-foreground">Bekleyen davet yok.</p>
          )}
          <ul className="divide-y">
            {q.data?.data.map((i) => (
              <li key={i.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                <div>
                  <div>{i.email}</div>
                  <div className="text-xs text-muted-foreground">
                    {ROLE_LABEL[i.role]} · Süre dolumu: {formatDate(i.expires_at)}
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => revoke.mutate(i.id)}>
                  <Trash2 className="size-4" />
                </Button>
              </li>
            ))}
          </ul>
        </div>
      </CardContent>
    </Card>
  )
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = React.useState(false)
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={async () => {
        await navigator.clipboard.writeText(text)
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
      }}
    >
      {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
      <span className="ml-1">{copied ? "Kopyalandı" : "Kopyala"}</span>
    </Button>
  )
}
